import type * as THREE from 'three'
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { AssemblyMateDefinition } from '../../assembly/definition'
import type { AssemblyMateDocumentJSON } from '../../assembly/document'
import type { GlbHierarchyNode, GlbHierarchyNodeMeta, ImportedSemanticKind } from '../../imported-model/loaders/glb'
import type { FileInfo } from '../../imported-model/loaders/file-info'
import type { ImportedModelResult, ImportedTopologySource, Matrix4Tuple } from '../../imported-model/loaders/types'
import type {
  DatumAxisFeatureHistoryRecord,
  DatumAxisHistoryRecord,
  DatumPlaneHistoryRecord,
  DatumPointFeatureHistoryRecord,
  DatumPointHistoryRecord,
} from '../../reference'
import type { BRepRegistrar } from '../../worker'
import type { AssemblyDocument } from '../../project-model/assembly-document/assembly-document-types'
import type { ProjectModelLoadResult } from '../../project-model/loading/project-model-load-session'
import { PUPPY_BREP_ASCII_ENCODING } from '../../constants/brep-protocol'
export { PUPPY_BREP_ASCII_ENCODING }
//------ 线性零部件阵列离线包定义持久化 开始------
import type { LinearComponentPatternPersistenceDocument } from '../../pattern/LinearComponentPatternPersistence'
//------ 草图离线包定义持久化 开始------
import type { PuppySketchExtension, PuppySketchExtensionEntry } from './puppySketchExtension'
//------ 草图离线包定义持久化 结束------
//------ 特征离线包定义持久化 开始------
import type { PuppyFeatureExtension, PuppyFeatureExtensionInput } from './puppyFeatureExtension'
//------ 特征离线包定义持久化 结束------
//------ 线性零部件阵列离线包定义持久化 结束------

export type {
  ExportPuppyPackageOptions,
  ImportPuppyPackageResult,
} from '../../../types/export'

/**
 * PuppyCAD 离线包格式名。
 */
export const PUPPY_PACKAGE_FORMAT = 'PuppyCAD Offline Package'

/**
 * PuppyCAD 当前容器协议版本。
 *
 * 容器版本只描述 ZIP 内文件清单。assembly、BRep 索引和 topology runtime
 * 保持各自文档版本，避免新增一个 sidecar 时迫使所有无关文档同步升级。
 */
export const PUPPY_CONTAINER_VERSION = 6

/**
 * 首个携带 project-model.json 的历史容器版本。
 *
 * @deprecated 仅用于读取历史 v5 离线包；兼容代码计划于 2026-08-11 移除，届时只接受 v6。
 */
export const PUPPY_PROJECT_MODEL_CONTAINER_VERSION = 5

/**
 * PuppyCAD 可读取的旧容器协议版本。
 *
 * @deprecated 仅用于读取历史 v4 离线包；兼容代码计划于 2026-08-11 移除，届时只接受 v6。
 */
export const PUPPY_LEGACY_CONTAINER_VERSION = 4

/**
 * PuppyCAD 既有语义文档版本。
 */
export const PUPPY_DOCUMENT_VERSION = 4

/**
 * PuppyCAD 既有语义文档版本兼容别名。
 *
 * @deprecated 新代码应根据目标文档使用独立版本常量；该别名仅保持既有调用方兼容。
 */
export const PUPPY_PACKAGE_VERSION = PUPPY_DOCUMENT_VERSION

/**
 * PuppyCAD 项目 occurrence 文档版本。
 */
export const PUPPY_PROJECT_MODEL_VERSION = 1

//------ 离线包基准扩展 开始------
/**
 * PuppyCAD 离线包基准扩展协议版本。
 */
export enum PuppyDatumExtensionSchemaVersion {
  /** 初始版本：保存文档级用户基准点、基准轴、基准面和可复算定义列表。 */
  V1 = 1,
}
//------ 离线包基准扩展 结束------

/**
 * 离线包清单路径。
 */
export const PUPPY_MANIFEST_PATH = 'manifest.json'

/**
 * 离线包显示快照 GLB 路径。
 */
export const PUPPY_DISPLAY_GLB_PATH = 'display.glb'

/**
 * 离线包 BRep 索引路径。
 */
export const PUPPY_BREP_INDEX_PATH = 'brep-index.json'

/**
 * 离线包装配结构语义路径。
 */
export const PUPPY_ASSEMBLY_PATH = 'assembly.json'

/**
 * 离线包可编辑项目 occurrence 文档路径。
 */
export const PUPPY_PROJECT_MODEL_PATH = 'project-model.json'

/**
 * 离线包交互拓扑快照路径。
 */
export const PUPPY_TOPOLOGY_RUNTIME_PATH = 'topology-runtime.bin'

/**
 * 离线包交互拓扑快照二进制编码。
 */
export const PUPPY_TOPOLOGY_RUNTIME_ENCODING = 'puppy_topology_runtime_packed_binary_v1'

/**
 * 离线包 BRep 文件目录。
 */
export const PUPPY_BREP_DIRECTORY = 'brep'

/**
 * PuppyCAD 二进制导出结果。
 */
export interface PuppyBinaryExportResult {
  /** 导出文件名。 */
  fileName: string
  /** MIME 类型。 */
  mimeType: string
  /** 文件字节。 */
  bytes: Uint8Array
}

/**
 * 本次 `.puppy` 导出根直系 occurrence 的内存身份。
 *
 * 该身份只用于同次提交事务把运行项目节点回连到最终 assemblyNodeId，禁止写入 ZIP 协议。
 */
export interface PuppyExportedRootOccurrenceIdentity {
  /** 本次运行项目树中的根直系节点 key。 */
  readonly sourceNodeKey: string
  /** 最终写入包内 assembly.json 的稳定节点 ID。 */
  readonly assemblyNodeId: string
}

/**
 * `.puppy` 离线包导出结果。
 */
export interface PuppyPackageExportResult extends PuppyBinaryExportResult {
  /** 本次导出根直系 occurrence 到最终装配身份的内存 provenance。 */
  readonly rootDirectOccurrenceIdentities: readonly PuppyExportedRootOccurrenceIdentity[]
}

/**
 * 可见模型 GLB 节点与 Worker 几何对象的绑定。
 */
export interface PuppyDisplayPrimitiveFaceRange {
  /** 离线包内部稳定 primitive ID。 */
  primitiveId: string
  /** Three.js raycast 返回的起始 faceIndex。 */
  startFaceIndex: number
  /** Three.js raycast 返回的结束 faceIndex，包含该索引。 */
  endFaceIndex: number
}

export interface PuppyDisplayNodeBodyBinding {
  /** Worker GeometryState 中已注册的权威 BRep 几何对象 ID；禁止使用 topo/pick metadata ID 回退。 */
  geometryObjectId: string
  /** 导入模型运行时使用的 body 绑定 ID。 */
  bodyBindingId: string
  /** 设计树与交互语义中的 body ID。 */
  bodyId: string
  /** body 显示名称。 */
  name?: string
  /** 当前 body 覆盖的 primitive ID 列表。 */
  primitiveIds: string[]
  /** 当前 body 覆盖的 GLB primitive 下标列表。 */
  primitiveIndices: number[]
  /** primitive 到可见 mesh faceIndex 范围的显式映射。 */
  primitiveFaceRanges: PuppyDisplayPrimitiveFaceRange[]
  /** 当前导出 Mesh 的局部三角形到完整 Body 面的明确映射；与 Body 拾取几何编号相互独立。 */
  topologyFaceTriangleRanges?: import('../../imported-model/loaders/types').ImportedTopologyFaceTriangleRangeBinding[]
}

export interface PuppyDisplayNodeBinding {
  /** GLB 节点稳定 ID，写入 userData.puppy.displayNodeId。 */
  displayNodeId: string
  /** 当前 display node 所属的稳定装配节点 ID；用于重载后恢复组件 transform 边界。 */
  assemblyNodeId?: string
  /** GLB 节点名称。 */
  nodeName: string
  /** 对应 Worker GeometryState 中的几何对象 ID。 */
  geometryObjectIds: string[]
  /** 当前 GLB 节点包含的 body 级拓扑绑定。 */
  bodyBindings: PuppyDisplayNodeBodyBinding[]
}

/**
 * 离线包装配树节点。
 */
export interface PuppyAssemblyNode {
  /** 离线包内部稳定装配节点 ID。*/
  nodeId: string
  /** 父装配节点 ID；根节点为 null。*/
  parentNodeId: string | null
  /** 设计树显示名称。*/
  name: string
  /** Three.js 节点类型或业务节点类型。*/
  nodeType: string
  /** 离线包装配树稳定路径。*/
  path: string
  /** 当前节点是否可作为设计树/选择语义节点。*/
  selectable: boolean
  /** 当前节点语义类型。*/
  semanticKind?: ImportedSemanticKind
  /** 导出前源 Object3D 的 uuid，用于调试和诊断。*/
  sourceObjectId?: string
  /**
   * 装配节点相对父节点的本地矩阵。
   *
   * 设计说明：配合文档中的 component transform 以装配节点为刚体根。
   * 离线包必须持久化同一层级的本地矩阵，避免导入时显示子树已经保留世界矩阵、
   * 而配合控制器再次把 component transform 写入根节点造成初始位置二次叠加。
   */
  transform?: Matrix4Tuple
  /** imported-model 层级元数据快照。*/
  meta?: GlbHierarchyNodeMeta
  /** 当前装配节点覆盖的 display.glb 节点 ID。*/
  displayNodeIds: string[]
  /** 当前装配节点覆盖的 body 绑定 ID。*/
  bodyBindingIds: string[]
  /** 当前装配节点覆盖的 BRep 几何对象 ID。*/
  geometryObjectIds: string[]
  /** 面向后续装配配合、约束、配置等扩展数据。*/
  extensions?: Record<string, unknown>
}

//------ 离线包基准扩展 开始------
/**
 * 离线包基准点记录。
 */
export type PuppyDatumPointRecord = DatumPointHistoryRecord

/**
 * 离线包基准点可复算特征记录。
 */
export type PuppyDatumPointFeatureRecord = DatumPointFeatureHistoryRecord

/**
 * 离线包基准轴记录。
 */
export type PuppyDatumAxisRecord = DatumAxisHistoryRecord

/**
 * 离线包基准轴可复算特征记录。
 */
export type PuppyDatumAxisFeatureRecord = DatumAxisFeatureHistoryRecord

/**
 * 离线包基准面记录。
 */
export type PuppyDatumPlaneRecord = DatumPlaneHistoryRecord

/**
 * 离线包基准扩展输入。
 *
 * 设计说明：
 * 1. 字段使用 `datumPointList` 等列表语义命名，避免把 Undo/Redo 的 Snapshot 概念泄漏到离线包协议；
 * 2. 正式对象列表保存稳定 ID、名称、几何缓存和显示状态；
 * 3. 点/轴 feature 列表保存可复算定义，恢复后装配体移动和拓扑重算仍有真相源。
 */
export interface PuppyDatumExtensionInput {
  /** 用户创建的正式基准点列表。 */
  readonly datumPointList?: readonly PuppyDatumPointRecord[]
  /** 用户创建的基准点特征定义列表。 */
  readonly datumPointFeatureList?: readonly PuppyDatumPointFeatureRecord[]
  /** 用户创建的正式基准轴列表。 */
  readonly datumAxisList?: readonly PuppyDatumAxisRecord[]
  /** 用户创建的基准轴特征定义列表。 */
  readonly datumAxisFeatureList?: readonly PuppyDatumAxisFeatureRecord[]
  /** 用户创建的正式基准面列表。 */
  readonly datumPlaneList?: readonly PuppyDatumPlaneRecord[]
}

/**
 * 离线包基准扩展。
 */
export interface PuppyDatumExtension {
  /** 基准扩展协议版本。 */
  readonly schemaVersion: PuppyDatumExtensionSchemaVersion.V1
  /** 用户创建的正式基准点列表。 */
  readonly datumPointList: PuppyDatumPointRecord[]
  /** 用户创建的基准点特征定义列表。 */
  readonly datumPointFeatureList: PuppyDatumPointFeatureRecord[]
  /** 用户创建的正式基准轴列表。 */
  readonly datumAxisList: PuppyDatumAxisRecord[]
  /** 用户创建的基准轴特征定义列表。 */
  readonly datumAxisFeatureList: PuppyDatumAxisFeatureRecord[]
  /** 用户创建的正式基准面列表。 */
  readonly datumPlaneList: PuppyDatumPlaneRecord[]
}
//------ 离线包基准扩展 结束------

/**
 * 离线包装配配合条目。
 *
 * 设计说明：
 * `.puppy` 的 `assembly.json.mates` 直接保存 cad-core Mate 文档 JSON；
 * 该别名只表达其中单条 Mate Definition 的元素类型，不再保留旧占位结构。
 */
export type PuppyAssemblyMate = AssemblyMateDefinition

/**
 * 离线包装配结构文档。
 */
export interface PuppyAssemblyDocument {
  /** 文档协议版本。*/
  version: typeof PUPPY_PACKAGE_VERSION
  /** 根装配节点 ID。*/
  rootNodeId: string
  /** 扁平节点表，方便导入端一次建索引。*/
  nodes: PuppyAssemblyNode[]
  /** 父节点到子节点 ID 列表的显式映射。*/
  childIdsByNodeId: Record<string, string[]>
  /** 装配体标准配合文档 JSON。*/
  mates: AssemblyMateDocumentJSON
  /** 文档级扩展字段。*/
  extensions?: Record<string, unknown>
}

/**
 * 可见模型导出诊断。
 */
export interface PuppyVisibleModelExportDiagnostics {
  /** 导出的 mesh 数量。 */
  exportedMeshCount: number
  /** 因不可见而跳过的 mesh 数量。 */
  skippedInvisibleMeshCount: number
  /** 因缺少渲染几何而跳过的 mesh 数量。 */
  skippedEmptyMeshCount: number
  /** 因属于辅助、预览或拓扑代理而跳过的对象数量。 */
  skippedNonModelObjectCount: number
  /** 缺少 BRep 绑定的可见模型 mesh 数量。 */
  missingBrepBindingMeshCount: number
  /** 缺少 primitive/body 级精确映射的可见 mesh 数量。 */
  ambiguousBrepBindingMeshCount: number
  /** 缺少可直接导入消费的拓扑快照的可见 mesh 数量。 */
  missingTopologySnapshotMeshCount: number
}

/**
 * `.puppy` 离线包对应的 CAD 文档类型。
 */
export type PuppyPackageDocumentType = 'part' | 'assembly'

/**
 * 离线包项目 occurrence 引用的版本身份。
 */
export interface PuppyProjectModelVersionReference {
  /** occurrence 引用的仓库 ID。 */
  readonly repositoryId: string
  /** occurrence 引用的分支 ID；包内本地节点可以省略。 */
  readonly branchId?: string
  /** occurrence 采用的提交 ID。 */
  readonly commitId: string
}

/**
 * 离线包中的单个可编辑项目 occurrence。
 *
 * `occurrenceId`、`assemblyNodeId` 和运行期 `projectNodeKey` 是三个不同身份：
 * occurrenceId 只在本项目文档内表达父子关系，assemblyNodeId 负责跨文档连接 CAD
 * 装配语义，projectNodeKey 则在加载时按当前根路径重新派生，不进入持久化协议。
 */
export interface PuppyProjectModelOccurrence {
  /** 项目文档内稳定 occurrence ID。 */
  readonly occurrenceId: string
  /** 父 occurrence ID；根 occurrence 为 null。 */
  readonly parentOccurrenceId: string | null
  /** 对应 `assembly.json.nodes[].nodeId` 的显式绑定。 */
  readonly assemblyNodeId: string
  /** 后端 `_assembly.json` 中的持久化节点 ID。 */
  readonly persistedNodeId?: string
  /** 后端 `_assembly.json` 中的实例 ID。 */
  readonly instanceId?: string
  /** occurrence 显示名称。 */
  readonly name: string
  /** occurrence 的 CAD 节点类型。 */
  readonly nodeType: PuppyPackageDocumentType
  /** occurrence 的可选版本引用。 */
  readonly versionReference?: PuppyProjectModelVersionReference
}

/**
 * 离线包可编辑项目 occurrence 文档。
 */
export interface PuppyProjectModelDocument {
  /** 项目 occurrence 文档协议版本。 */
  readonly version: typeof PUPPY_PROJECT_MODEL_VERSION
  /** 根 occurrence ID。 */
  readonly rootOccurrenceId: string
  /** 按前序排列的 occurrence 列表。 */
  readonly occurrences: readonly PuppyProjectModelOccurrence[]
}

/**
 * 项目 occurrence 文档及同次导出 provenance。
 */
export interface PuppyProjectModelExportResult {
  /** 可写入 ZIP 的项目 occurrence 文档。 */
  readonly document: PuppyProjectModelDocument
  /** 仅供外层提交事务消费的根直系 occurrence 身份。 */
  readonly rootDirectOccurrenceIdentities: readonly PuppyExportedRootOccurrenceIdentity[]
}

/**
 * 项目 occurrence 文档导出源。
 *
 * `loadResult.runtimeState` 提供项目节点到最终场景装配身份的统一关系，`assemblyDocument`
 * 提供同一次提交使用的协同装配草稿。创建器会校验二者一致后才把版本引用写入离线包。
 */
export interface PuppyProjectModelExportSource {
  /** 当前项目模型加载结果。 */
  readonly loadResult: ProjectModelLoadResult
  /** 当前提交使用的装配工作文档。 */
  readonly assemblyDocument: AssemblyDocument
}

/**
 * 可见模型 GLB 导出根。
 */
export interface PuppyVisibleModelExportRoot {
  /** 被 GLTFExporter 消费的临时根节点。 */
  root: THREE.Group
  /** GLB 节点到 Worker 几何对象的绑定。 */
  nodeBindings: PuppyDisplayNodeBinding[]
  /** body 级交互拓扑资产。 */
  bodyTopologyAssets: PuppyVisibleBodyTopologyAsset[]
  /** 可扩展装配结构语义。 */
  assemblyDocument: PuppyAssemblyDocument
  /**
   * 导出遍历中被折叠掉的 imported-model 语义节点。
   *
   * 这些节点不进入最终 assembly.json 设计树，只作为 Mate 文档节点引用重映射的身份来源。
   */
  foldedAssemblyNodes?: PuppyAssemblyNode[]
  /**
   * 导出遍历中源 Object3D 的本地矩阵索引。
   *
   * 设计说明：权威 hierarchy 可能包含不直接拥有 display/body 的装配节点。
   * 这些节点仍然需要按原工程姿态写入 assembly.json，因此必须用 hierarchy.objectId
   * 回查源 Object3D 的本地矩阵，不能依赖 display 归属兜底。
   */
  sourceObjectLocalTransforms?: Record<string, Matrix4Tuple>
  /**
   * 最终装配节点到当前导出事务 Object3D UUID 的绑定。
   *
   * 该索引只供同一事务生成 project-model.json，禁止写入离线包文件。
   */
  runtimeSourceObjectIdsByAssemblyNodeId?: Readonly<Record<string, readonly string[]>>
  /** 导出诊断。 */
  diagnostics: PuppyVisibleModelExportDiagnostics
}

/**
 * PuppyCAD 离线包清单。
 */
/**
 * 可见模型导出阶段收集到的 body 级交互拓扑资产。
 */
export interface PuppyVisibleBodyTopologyAsset {
  /** 导入运行时使用的 body 绑定 ID。 */
  bodyBindingId: string
  /** Worker GeometryState 中已注册的权威 BRep 几何对象 ID；禁止使用 topo/pick metadata ID 回退。 */
  geometryObjectId: string
  /** STEP 导出复用同一份 BRep 定义的稳定 ID。 */
  exportDefinitionId?: string
  /** 可直接交给 imported-model 拾取仓库消费的网格与拓扑快照。 */
  meshData: MeshData
}

/**
 * 写入 `.puppy` 的 body 级交互拓扑资产。
 */
export interface PuppyTopologyRuntimeAssetPayload {
  /** body 绑定 ID。 */
  bodyBindingId: string
  /** Worker GeometryState 中的 BRep 几何对象 ID。 */
  geometryObjectId: string
  /** STEP 导出复用同一份 BRep 定义的稳定 ID。 */
  exportDefinitionId?: string
  /** body 语义 ID。 */
  bodyId?: string
  /** 面到 GLB 三角形范围的映射，结束索引为开区间。 */
  faceTriangleRanges?: [number, number][]
  /** 拓扑资产元数据。 */
  metadata?: Record<string, unknown>
  /** 权威拓扑快照。 */
  topologySnapshot: TopologySnapshot
}

/**
 * `.puppy` 内部交互拓扑快照文档。
 */
export interface PuppyTopologyRuntimeDocument {
  /** 文档协议版本。 */
  version: typeof PUPPY_PACKAGE_VERSION
  /** body 级交互拓扑资产列表。 */
  assets: PuppyTopologyRuntimeAssetPayload[]
}

/**
 * 解析后的 body 级交互拓扑资产。
 */
export interface PuppyParsedTopologyRuntimeAsset {
  /** body 绑定 ID。 */
  bodyBindingId: string
  /** Worker GeometryState 中的 BRep 几何对象 ID。 */
  geometryObjectId: string
  /** STEP 导出复用同一份 BRep 定义的稳定 ID。 */
  exportDefinitionId?: string
  /** body 语义 ID。 */
  bodyId?: string
  /** 面到 GLB 三角形范围的映射，结束索引为开区间。 */
  faceTriangleRanges?: [number, number][]
  /** 拓扑资产元数据。 */
  metadata?: Record<string, unknown>
  /** 权威拓扑快照。 */
  topologySnapshot: TopologySnapshot
}

/**
 * 解析后的交互拓扑快照文档。
 */
export interface PuppyParsedTopologyRuntimeDocument {
  /** 文档协议版本。 */
  version: typeof PUPPY_PACKAGE_VERSION
  /** body 级交互拓扑资产列表。 */
  assets: PuppyParsedTopologyRuntimeAsset[]
}

export interface PuppyPackageManifest {
  /** 容器格式名。 */
  format: typeof PUPPY_PACKAGE_FORMAT
  /** 容器协议版本。 */
  version:
    | typeof PUPPY_CONTAINER_VERSION
    | typeof PUPPY_PROJECT_MODEL_CONTAINER_VERSION
    | typeof PUPPY_LEGACY_CONTAINER_VERSION
  /** 包内容 ID；v6 由全部非 manifest 业务文件摘要确定。 */
  packageId: string
  /** 项目或模型名称。 */
  projectName: string
  /** 导出时的 CAD 文档类型，用于导入时恢复零件/装配体工作模式。 */
  documentType: PuppyPackageDocumentType
  /** @deprecated 历史 v4/v5 生成时间戳；读取兼容计划于 2026-08-11 移除。 */
  exportedAt?: string
  /** v6 非 manifest 业务文件的规范内容摘要。 */
  contentDigest?: string
  /** 显示快照 GLB 路径。 */
  displayFile: typeof PUPPY_DISPLAY_GLB_PATH
  /** BRep 索引文件路径。 */
  brepIndexFile: typeof PUPPY_BREP_INDEX_PATH
  /** 装配结构文件路径。 */
  assemblyFile: typeof PUPPY_ASSEMBLY_PATH
  /** 可编辑项目 occurrence 文档路径；v4 容器和零件包可以省略。 */
  projectModelFile?: typeof PUPPY_PROJECT_MODEL_PATH
  /** 交互拓扑快照文件路径。 */
  topologyRuntimeFile: typeof PUPPY_TOPOLOGY_RUNTIME_PATH
  /** 交互拓扑快照编码。 */
  topologyRuntimeEncoding: typeof PUPPY_TOPOLOGY_RUNTIME_ENCODING
  /** 显示 mesh 数量。 */
  visibleMeshCount: number
  /** BRep 文件数量。 */
  brepFileCount: number
  /** Worker 几何对象数量。 */
  geometryObjectCount: number
  /** 导出诊断。 */
  diagnostics: PuppyVisibleModelExportDiagnostics
}

/**
 * 离线包中的单个 BRep 文件条目。
 */
export interface PuppyBrepIndexEntry extends PuppyBrepShapeScopeDescriptor {
  /** Worker GeometryState 中的几何对象 ID。 */
  geometryObjectId: string
  /** 可读显示名称。 */
  name?: string
  /** BRep payload 编码。 */
  encoding: typeof PUPPY_BREP_ASCII_ENCODING
  /** BRep 文件 SHA-256。 */
  brepSha256: string
  /** BRep 文件在 ZIP 内部的路径。 */
  brepPath: string
  /** BRep 文件字节长度。 */
  byteLength: number
  /** 创建该对象的命令 ID；导入基线对象可为空。 */
  commandId?: string
}

/**
 * 离线包 BRep 索引文档。
 */
export interface PuppyBrepIndexDocument {
  /** 索引协议版本。 */
  version: typeof PUPPY_PACKAGE_VERSION
  /** GLB 节点与 Worker 几何对象的绑定。 */
  displayNodes: PuppyDisplayNodeBinding[]
  /** BRep 文件条目。 */
  entries: PuppyBrepIndexEntry[]
}

/**
 * 解析出的 BRep 文件。
 */
export interface PuppyParsedBrepFile {
  /** ZIP 内部路径。 */
  path: string
  /** 文件字节。 */
  bytes: Uint8Array
  /** BRep 文本。 */
  payload: string
}

/**
 * PuppyCAD 离线包解析结果。
 */
export interface PuppyPackageParseResult {
  /** 清单。 */
  manifest: PuppyPackageManifest
  /** BRep 索引。 */
  brepIndex: PuppyBrepIndexDocument
  /** 装配结构语义。 */
  assembly: PuppyAssemblyDocument
  /** 可编辑项目 occurrence 文档；v4 容器和零件包为 null。 */
  projectModel: PuppyProjectModelDocument | null
  /** 交互拓扑快照。 */
  topologyRuntime: PuppyParsedTopologyRuntimeDocument
  /** 显示快照 GLB 字节。 */
  displayGlbBytes: Uint8Array
  /** 按 SHA-256 索引的 BRep 文件。 */
  brepFilesBySha256: Record<string, PuppyParsedBrepFile>
}

/**
 * PuppyCAD 离线包导入 BRep 注册能力兼容别名。
 *
 * @deprecated 新代码请直接依赖 `BRepRegistrar`，避免把导入链路绑定到具体 WorkerManager。
 */
export interface PuppyPackageImportWorkerClient extends BRepRegistrar {}

/**
 * PuppyCAD 离线包导入选项。
 */
export interface ImportPuppyPackageOptions {
  /** BRep Shape 最小注册能力；导入包含 BRep 时必须提供。 */
  brepRegistrar?: BRepRegistrar
  /**
   * 旧 Worker 依赖兼容别名。
   *
   * @deprecated 请改用 `brepRegistrar`，后续 Viewer document runtime switch 会传入 staged registrar。
   */
  workerManager?: PuppyPackageImportWorkerClient
  /** 可选 GLTFLoader；缺省时 cad-core 内部创建。 */
  gltfLoader?: GLTFLoader
  /** 可选文件身份；项目模型加载需要沿用后端下发的 fileId/uuid/kind。 */
  fileInfo?: FileInfo
  /** 导入文件名，用于显示根节点命名兜底。 */
  packageName?: string
}

/**
 * `.puppy` 导入后可用于 UI 注册的虚拟模型 tab 模板。
 */
export interface PuppyImportedModelTabTemplate {
  /** assembly.json 中的稳定节点 ID。 */
  nodeId: string
  /** tab 显示名称。 */
  label: string
  /** tab 语义类型。 */
  type: 'assembly' | 'part'
  /** tab 图标类型。 */
  iconType: 'assembly' | 'part'
  /** 当前 tab 对应的运行时 Object3D UUID；根 tab 为 `null`。 */
  scopeObjectId: string | null
  /** 是否为离线包根 tab。 */
  isRoot: boolean
}

/**
 * `.puppy` 导入阶段耗时统计。
 */
export interface PuppyImportTiming {
  /** 解压、校验与解析 `.puppy` 容器耗时。 */
  parsePackageMs: number
  /** 解析 display.glb 耗时。 */
  parseDisplayGlbMs: number
  /** BRep Shape-only 注册耗时。 */
  registerBrepShapesMs: number
  /** 构建 imported-model 拓扑源耗时。 */
  buildTopologySourceMs: number
  /** 构建设计树与激活拓扑仓库耗时。 */
  activateRuntimeMs: number
  /** 总耗时。 */
  totalMs: number
}

/**
 * PuppyCAD 离线包导出 Worker 依赖。
 */
export interface PuppyPackageExportWorkerClient {
  /**
   * 导出 live BRep Shape。
   *
   * @param payload 导出请求。
   * @returns 导出结果。
   */
  exportBrepShapes(payload: ExportBrepShapesRequest): Promise<ExportBrepShapesResult>
}

/**
 * PuppyCAD 离线包装配语义导出源。
 *
 * 设计说明：
 * 1. `.puppy` 的可见几何仍然来自当前 Three.js 场景，保证导出内容和视口一致；
 * 2. 装配树、零件刚体边界和底部 tab 语义必须来自编辑器已经确认的权威 hierarchy；
 * 3. 这里使用判别联合保留扩展点，后续新增原生工程状态、PMI、爆炸视图等导出语义时只需要扩展新的 kind。
 */
export type PuppyExportAssemblySemanticSource =
  | {
    /** 语义来自 cad-core imported-model 加载结果。 */
    kind: 'imported-model-result'
    /** 当前活动标签对应的导入模型结果。 */
    result: ImportedModelResult
  }
  | {
    /** 语义来自调用方直接传入的 hierarchy。 */
    kind: 'hierarchy'
    /** 当前导出应采用的权威设计树 hierarchy。 */
    hierarchy: GlbHierarchyNode
  }

/**
 * PuppyCAD 离线包原生结果体显示名映射。
 */
export type PuppyExportResultBodyDisplayNameMap = ReadonlyMap<string, string>

/**
 * GLB 导出选项。
 */
export interface ExportGlbOptions {
  /** 下载文件基础名称。 */
  fileBaseName: string
  /** 当前 Viewer 场景根。 */
  sceneRoot: THREE.Object3D
}
